import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { getAuctionPackages, applyAuction } from "@/lib/api";

const DOG_IMG = "https://images.unsplash.com/photo-1568572933382-74d440642117?w=1400&q=85&auto=format&fit=crop";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(target).getTime();
  const diff = Math.max(0, end - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff === 0 };
}

function pkgAccent(slug) {
  if (slug === "chicago-2026") return { grad: "from-[#7a1220] via-[#c9a24b] to-[#e8c876]", chip: "bg-[#c9a24b] text-black" };
  if (slug === "private-elite") return { grad: "from-[#0f5d3a] via-[#1a7a4f] to-[#e8c876]", chip: "bg-[#1a7a4f] text-white" };
  return { grad: "from-[#4b2e83] via-[#6b4bb2] to-[#e8c876]", chip: "bg-[#6b4bb2] text-white" };
}

export default function AuctionSection() {
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // package slug string
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", dog_name: "", breed: "", bid_amount: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getAuctionPackages();
      setPkgs(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // refresh inventory every 30s
    return () => clearInterval(t);
  }, [load]);

  const chicago = pkgs.find((p) => p.slug === "chicago-2026");
  const cd = useCountdown(chicago?.window_end || "2026-11-27T23:59:59+00:00");

  const openModal = (slug) => {
    setModal(slug);
    setForm({ name: "", email: "", phone: "", city: slug === "chicago-2026" ? "Chicago" : "", dog_name: "", breed: "", bid_amount: "", message: "" });
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await applyAuction({ ...form, package_slug: modal });
      toast.success("Application received. Toriano's team will call you personally.");
      setModal(null);
      setForm({ name: "", email: "", phone: "", city: "", dog_name: "", breed: "", bid_amount: "", message: "" });
      load();
    } catch (err) {
      if (err?.response?.status === 409) toast.error("This package just filled. Choose another package or call 312-933-1528.");
      else toast.error("Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  const active = pkgs.find((p) => p.slug === modal);

  return (
    <section id="auction" className="relative overflow-hidden" data-testid="auction-section">
      {/* Bright layered background — cream base with color washes */}
      <div className="absolute inset-0" style={{
        background: "linear-gradient(180deg, #f7f1e5 0%, #f2ece1 40%, #ffffff 100%)",
      }} />
      <div className="absolute inset-0" style={{
        background: "radial-gradient(700px 500px at 12% 0%, rgba(75,46,131,0.18), transparent 60%), radial-gradient(600px 500px at 90% 30%, rgba(15,93,58,0.16), transparent 60%), radial-gradient(600px 500px at 50% 100%, rgba(201,162,75,0.28), transparent 60%)",
      }} />

      <div className="relative z-10 max-w-[1600px] mx-auto px-5 md:px-10 py-16 md:py-24">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="flex items-center gap-3 justify-center md:justify-start"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-black text-[#e8c876] px-4 py-2 text-[10px] tracking-[0.28em] uppercase font-semibold">
            <span className="w-2 h-2 rounded-full bg-[#e8c876] animate-pulse"/> Live · Global Wolfkeeper Auction
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-6 text-center md:text-left font-display text-[clamp(2.4rem,8vw,7rem)] leading-[0.92] tracking-[-0.03em] text-[#0a0908]"
          data-testid="auction-headline"
        >
          Train exclusively with{" "}
          <span className="italic" style={{
            background: "linear-gradient(90deg,#7a1220 0%, #c9a24b 45%, #4b2e83 100%)",
            WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
          }}>Toriano Sanzone.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
          className="mt-5 md:mt-6 text-center md:text-left text-lg md:text-2xl text-[#1a1917]/85 max-w-3xl md:mx-0 mx-auto font-editorial italic"
        >
          Bid for the city. Secure your certificate. Transform your dog. Train with the Wolfkeeper.
        </motion.p>

        {/* Chicago urgent banner */}
        {chicago && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-8 md:mt-10 rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(122,18,32,0.5)] relative"
            data-testid="chicago-banner"
          >
            <div className="grid md:grid-cols-5">
              {/* Image */}
              <div className="md:col-span-2 relative min-h-[220px] md:min-h-[420px]">
                <img src={DOG_IMG} alt="Elite dog training" className="absolute inset-0 w-full h-full object-cover"/>
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.35) 100%)" }} />
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-black">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"/> Final 2026
                  </span>
                </div>
                {/* Live remaining */}
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <div className="bg-white rounded-2xl px-4 py-3 shadow-lg">
                    <div className="text-[9px] tracking-[0.28em] uppercase text-black/60">Remaining</div>
                    <div className="stat-num text-3xl md:text-4xl text-black" data-testid="chicago-remaining">
                      {loading ? "—" : `${chicago.remaining_slots} / ${chicago.total_slots}`}
                    </div>
                  </div>
                  <div className="bg-[#c9a24b] rounded-2xl px-4 py-3 shadow-lg text-black text-right">
                    <div className="text-[9px] tracking-[0.28em] uppercase">Opening bid</div>
                    <div className="stat-num text-2xl md:text-3xl">${chicago.opening_bid.toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Copy + countdown */}
              <div className="md:col-span-3 relative p-6 md:p-10 bg-gradient-to-br from-[#0a0908] via-[#1a1917] to-[#0a0908] text-[#f7f1e5]">
                <div className="absolute inset-0 opacity-[0.08]" style={{
                  background: "radial-gradient(500px 300px at 100% 0%, #c9a24b, transparent 60%), radial-gradient(400px 300px at 0% 100%, #4b2e83, transparent 60%)"
                }}/>
                <div className="relative">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[#e8c876]">Chicago · Boot Camp · Only 10 dogs</div>
                  <h3 className="mt-3 font-display text-3xl md:text-5xl leading-[0.95] tracking-[-0.02em]">
                    Final 2026 Chicago<br/>
                    <span className="italic" style={{
                      background: "linear-gradient(90deg,#e8c876,#c9a24b)",
                      WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent",
                    }}>Boot Camp.</span>
                  </h3>
                  <div className="mt-2 text-[#f7f1e5]/80 text-sm md:text-base">{chicago.window_label}</div>

                  {/* Countdown */}
                  <div className="mt-5 grid grid-cols-4 gap-2 md:gap-3" data-testid="countdown">
                    {[["Days", cd.d], ["Hours", cd.h], ["Min", cd.m], ["Sec", cd.s]].map(([l, v]) => (
                      <div key={l} className="bg-white/10 backdrop-blur border border-white/10 rounded-xl px-2 py-3 md:py-4 text-center">
                        <div className="stat-num text-2xl md:text-4xl text-[#f7f1e5]">{String(v).padStart(2, "0")}</div>
                        <div className="text-[9px] tracking-[0.24em] uppercase text-[#f7f1e5]/60 mt-1">{l}</div>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => openModal("chicago-2026")}
                    disabled={chicago.remaining_slots <= 0}
                    className="mt-6 w-full md:w-auto inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full text-[13px] font-semibold tracking-[0.22em] uppercase transition-all disabled:opacity-50"
                    style={{
                      background: "linear-gradient(90deg,#e8c876,#c9a24b)",
                      color: "#0a0908",
                      boxShadow: "0 20px 40px -15px rgba(201,162,75,0.5)",
                    }}
                    data-testid="chicago-apply"
                  >
                    Apply for Chicago Now
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Two large CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
          className="mt-8 md:mt-10 grid md:grid-cols-2 gap-3 md:gap-4"
        >
          <a
            href="#all-packages"
            onClick={(e) => { e.preventDefault(); document.getElementById("all-packages")?.scrollIntoView({ behavior: "smooth" }); }}
            className="group flex items-center justify-between gap-3 rounded-2xl px-6 py-5 md:py-6 text-black font-semibold tracking-[0.18em] uppercase text-[13px] md:text-[15px] transition-all"
            style={{
              background: "linear-gradient(135deg,#ffffff 0%,#f2ece1 60%,#e8c876 130%)",
              boxShadow: "0 20px 40px -20px rgba(201,162,75,0.4), inset 0 0 0 1px rgba(201,162,75,0.35)",
            }}
            data-testid="cta-view-packages"
          >
            <span className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-black text-[#e8c876] grid place-items-center text-lg">✦</span>
              View Live Training Packages
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </a>
          <button
            onClick={() => openModal("online-private")}
            className="group flex items-center justify-between gap-3 rounded-2xl px-6 py-5 md:py-6 text-white font-semibold tracking-[0.18em] uppercase text-[13px] md:text-[15px] transition-all"
            style={{
              background: "linear-gradient(135deg,#4b2e83 0%,#7a1220 100%)",
              boxShadow: "0 20px 40px -20px rgba(75,46,131,0.5)",
            }}
            data-testid="cta-apply-certificate"
          >
            <span className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-white/15 text-[#e8c876] grid place-items-center text-lg">◈</span>
              Apply for a Certificate
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
        </motion.div>
      </div>

      {/* Apply Modal */}
      <AnimatePresence>
        {modal && active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-md overflow-y-auto"
            onClick={() => setModal(null)}
            data-testid="auction-modal"
          >
            <div className="min-h-full flex items-center justify-center p-4 py-16">
              <motion.form
                onSubmit={submit}
                initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl"
                data-testid="auction-form"
              >
                <div className={`h-2 bg-gradient-to-r ${pkgAccent(active.slug).grad}`} />
                <div className="p-6 md:p-10">
                  <button type="button" onClick={() => setModal(null)} className="float-right text-black/50 hover:text-black" data-testid="auction-close">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-black/60">Apply · {active.city}</div>
                  <h3 className="mt-2 font-display text-3xl md:text-4xl text-black leading-tight">{active.title}</h3>
                  <div className="text-black/60 text-sm mt-1">{active.window_label} · Opening bid ${active.opening_bid.toLocaleString()} · {active.remaining_slots}/{active.total_slots} left</div>

                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    <label className="block">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Full name</span>
                      <input required value={form.name} onChange={upd("name")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-name"/>
                    </label>
                    <label className="block">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Phone</span>
                      <input required value={form.phone} onChange={upd("phone")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-phone"/>
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Email</span>
                      <input required type="email" value={form.email} onChange={upd("email")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-email"/>
                    </label>
                    <label className="block">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">City</span>
                      <input value={form.city} onChange={upd("city")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-city"/>
                    </label>
                    <label className="block">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Your bid (USD)</span>
                      <input value={form.bid_amount} onChange={upd("bid_amount")} placeholder={`$${active.opening_bid.toLocaleString()}+`} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-bid"/>
                    </label>
                    <label className="block">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Dog&apos;s name</span>
                      <input value={form.dog_name} onChange={upd("dog_name")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-dog"/>
                    </label>
                    <label className="block">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Breed</span>
                      <input value={form.breed} onChange={upd("breed")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none" data-testid="ax-breed"/>
                    </label>
                    <label className="block md:col-span-2">
                      <span className="text-[10px] tracking-[0.24em] uppercase text-black/60">Anything else?</span>
                      <textarea value={form.message} onChange={upd("message")} className="w-full mt-1 border-b border-black/20 py-2 text-black bg-transparent focus:border-black focus:outline-none min-h-[70px]" data-testid="ax-message"/>
                    </label>
                  </div>

                  <button type="submit" disabled={submitting} className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full py-4 font-semibold tracking-[0.22em] uppercase text-[13px]" style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)", color: "#0a0908" }} data-testid="ax-submit">
                    {submitting ? "Submitting…" : "Submit My Application"}
                  </button>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-black/50 mt-3 text-center">Real inventory · Concierge review · No fake scarcity</div>
                </div>
              </motion.form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
